import {parseJSON, parseKBSubdomain, publicChatData} from "./App/utils";
import {colorGreen, colorEasyRed} from "./App/theme";
import jwtDecode from "jwt-decode";
import yaml from "js-yaml";
import {generateChatId, generateId} from "./Chat/utils";

// export const RAW_FILES_URL = 'https://openkbs-files-raw.s3-accelerate.amazonaws.com';
export const RAW_FILES_URL = 'https://file.openkbs.com';
export function isMac() {
    return [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod'
        ].includes(window?.navigator?.platform)
        // iPad on iOS 13 detection
        || window?.navigator?.platform?.indexOf('Mac') > -1
}

export const parseJSONTool = (inputTools, type = 'imageEditor') => {
    return parseJSON(inputTools?.find?.(tool => parseJSON(tool)?.type === type));
}

export function formatScore(score) {
    return parseFloat((score * 100).toFixed(2))
}

export function toYAML(data) {
    return yaml.dump(data);
}

export function isValidURL (str) {
    return str.startsWith('http')
}

export function debounce(func, wait, immediate) {
    let timeout;
    return function () {
        const context = this, args = arguments;
        const later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

// just random number associated with the page loaded
export const pageRandomNumber = (Math.floor(Math.random() * 10**15) + 1);
export const pageLoadTime = +new Date();
export const pageLoadedIn = () => +new Date() - pageLoadTime;

export function uppercaseFirst(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getCountries() {
    const codes = [];
    const regionNames = new Intl.DisplayNames(['en-US'], { type: 'region' });

    for (let i = 65; i <= 90; i++) {
        for (let j = 65; j <= 90; j++) {
            const code = String.fromCharCode(i) + String.fromCharCode(j);
            try {
                const name = regionNames.of(code);
                if (name && name !== code) {
                    codes.push(code);
                }
            } catch (error) {
                console.error(`Error with code: ${code}`, error);
            }
        }
    }

    return codes
}

export function sortCountries(regionNames, countryCodes) {
    return countryCodes.sort((a, b) => {
        const nameA = regionNames.of(a).toUpperCase();
        const nameB = regionNames.of(b).toUpperCase();

        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });
}

export function isWindows() {
    return window?.navigator?.platform?.indexOf('Win') > -1
}

export function isLinux() {
    return window?.navigator?.platform?.indexOf('Linux') > -1
}

export function getPlatform() {
    if (isMac()) return 'mac'
    if (isLinux()) return 'linux'
    if (isWindows()) return 'windows'
    return window?.navigator?.platform
}

export function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const updateRecentApps = (kbId, title, maxRecentApps = 3) => {
    const currentTime = new Date().getTime(); // Get current timestamp
    const recentApps = JSON.parse(localStorage.getItem('recentApps')) || [];
    const filteredApps = recentApps.filter(app => app.kbId !== kbId);
    const updatedApps = [{ kbId, title, timestamp: currentTime }, ...filteredApps];
    if (updatedApps.length > maxRecentApps) updatedApps.length = maxRecentApps;
    localStorage.setItem('recentApps', JSON.stringify(updatedApps));
}

export const deleteRecentApp = (kbId) => {
    let recentApps = JSON.parse(localStorage.getItem('recentApps')) || [];
    const updatedApps = recentApps.filter(app => app.kbId !== kbId);
    localStorage.setItem('recentApps', JSON.stringify(updatedApps));
};

export const getRecentApps = (timeLimit = 1000*60*60*24*30) => {
    const currentTime = new Date().getTime();
    const recentApps = JSON.parse(localStorage.getItem('recentApps')) || [];

    return recentApps
        .filter(app => currentTime - app.timestamp <= timeLimit)
        .sort((a, b) => b.timestamp - a.timestamp);
};

export const clientUserData = () => {
    const token = localStorage.getItem('clientJWT');

    if (!token) return null;

    const data = jwtDecode(token);
    return {
        userId: data.userId,
    }
}

export const clientUserDataFull = () => {
    const token = localStorage.getItem('clientJWT');
    if (!token) return null;
    return jwtDecode(token);
}

export const kbUserData = () => {
    const token = localStorage.getItem('kbJWT');
    if (!token) return null;

    const data = jwtDecode(token);

    return {
        kbId: data.kbId,
        kbUserId: data.kbUserId,
        myUserId: data.myUserId,
        userPicture: data.userPicture,
        chatUsername: `User_${data?.myUserId}`,
        email: data?.email,
        name: data?.name
    }
}

export const clientUserDataLoaded = clientUserDataFull();
export const kbUserDataLoaded = kbUserData();
export const isSharedApp = kbUserDataLoaded && kbUserDataLoaded.kbUserId !== kbUserDataLoaded.myUserId;
export const isSharedChat = window?.location?.pathname?.startsWith('/share-chat');
export function findRefundedTransactions(transactions) {
    const refunded = {};
    const tempMap = {};
    transactions.forEach(transaction => {
        const { amount, subject, subjectId } = transaction;
        if (subject === "paymentIntent") {
            if (!tempMap[subjectId]) {
                tempMap[subjectId] = amount;
            } else {
                if (tempMap[subjectId] + amount === 0) {
                    refunded[subjectId] = true;
                }
            }
        }
    });
    return refunded;
}

export const formatDateTime = (timestamp) => {
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

export function getRandomBetween(min, max) {
    return Math.round(Math.random() * (max - min)) + min;
}

export function formatMoneyToString(num) {
    return num?.toFixed().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}
export function formatMoney(num, prefix = '', myColor) {
    const color = myColor ? myColor : num >= 0 ? colorGreen : colorEasyRed;
    return <span style={{color}}>{prefix + formatMoneyToString(num)}</span>
}

export function formatMoneyCents(cents, prefix = '', myColor) {
    const dollars = Number((cents / 100000).toFixed(2));
    const color = myColor || (cents >= 0 ? colorGreen : colorEasyRed);
    const formattedAmount = dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return <span style={{ color }}>{prefix + formattedAmount}</span>;
}

export function formatMoneyTextColor(num) {
    return num >= 0 ? colorGreen : colorEasyRed
}

export const goHome = (navigate) => {
    const {kbIdAsSubdomain, appsPrefix, mainDomain, protocol} = parseKBSubdomain() || {};
    if (kbIdAsSubdomain) {
        window.location.href = `${protocol}//${mainDomain}/dashboard`
    } else if (typeof navigate === 'function') {
        navigate('/dashboard');
    }
}

export const parseTS = (id) => {
    return parseInt(id?.split('-')[0])
}

export const goMainDomain = () => {
    const {kbIdAsSubdomain, appsPrefix, mainDomain, protocol} = parseKBSubdomain() || {};
    if (kbIdAsSubdomain) {
        window.location.href = `${protocol}//${mainDomain}`
    }
}

export const getBaseUrl = () => {
    const {kbIdAsSubdomain, appsPrefix, mainDomain, protocol} = parseKBSubdomain() || {};
    if (kbIdAsSubdomain) {
        return `${protocol}//${mainDomain}`
    } else {
        return `${window.location.protocol}//${window.location.host}`
    }
}